<template>
	<div class="cards-chart">
		<AppChart
			expandible
			height="340px"
			full-width
			:caption="getString('amount')"
			:value="chartData.totalAmount"
			:last-period-value="chartData.totalAmountPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="chartData.series"
			:xaxis="chartData.xaxis"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:key="refreshKey"
		/>
	</div>
</template>

<script>
import {useCardsStore} from "@/console-new/stores/cards.store";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import AppChart from "@/shared/components/AppChart.vue";

export default {
	components: {AppChart},
	setup() {
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {
			cardsStore,
			currencyStore,
			getString: languageStore.getString,
			userStore
		}
	},
	name: "CardsChart",
	data() {
		return {
			transactionData: null,
			loading: false,
			refreshKey: 0
		}
	},
	computed: {
		currentCard() {
			if (!this.cardsStore.businessCards || !this.cardsStore.activeCardId) {
				return null;
			}
			return this.cardsStore.getCardById(this.cardsStore.activeCardId);
		},
		chartData() {
			if (!this.transactionData || !this.transactionData.authorizations || !this.transactionData.transactions) {
				return {
					totalAmount: 0,
					totalAmountPrevious: 0,
					series: [],
					xaxis: []
				};
			}

			// Generate last 7 days for display
			const days = [];
			const today = new Date();
			for (let i = 6; i >= 0; i--) {
				const date = new Date(today);
				date.setDate(date.getDate() - i);
				days.push({
					date: date,
					label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
				});
			}

			// Generate previous 7 days (8-14 days ago)
			const previousDays = [];
			for (let i = 13; i >= 7; i--) {
				const date = new Date(today);
				date.setDate(date.getDate() - i);
				previousDays.push({
					date: date,
					label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
				});
			}

			// Initialize data arrays
			const currentPeriodData = new Array(7).fill(0);
			const previousPeriodData = new Array(7).fill(0);

			let totalAmount = 0;
			let totalAmountPrevious = 0;

			// Process authorizations (only pending, approved - exclude closed, expired, reversed)
			this.transactionData.authorizations.forEach(auth => {
				if (['closed', 'expired', 'reversed', 'declined'].includes(auth.status)) {
					return; // Skip these statuses
				}

				const authDate = auth.date; // Date is already normalized in store
				
				// Check current period
				const currentDayIndex = this.getDayIndex(authDate, days);
				if (currentDayIndex !== -1) {
					const amount = this.currencyStore.dbToActualValue(auth.amount);
					currentPeriodData[currentDayIndex] += amount;
					totalAmount += amount;
				}

				// Check previous period
				const previousDayIndex = this.getDayIndex(authDate, previousDays);
				if (previousDayIndex !== -1) {
					const amount = this.currencyStore.dbToActualValue(auth.amount);
					previousPeriodData[previousDayIndex] += amount;
					totalAmountPrevious += amount;
				}
			});

			// Process transactions (negative for captures, positive for refunds)
			this.transactionData.transactions.forEach(txn => {
				const txnDate = txn.date; // Date is already normalized in store
				
				// Convert amount from cents to actual currency value
				let amount = this.currencyStore.dbToActualValue(txn.amount);
				
				// Make captures negative, refunds positive
				if (txn.type === 'capture') {
					amount = -amount;
				}
				
				// Check current period
				const currentDayIndex = this.getDayIndex(txnDate, days);
				if (currentDayIndex !== -1) {
					currentPeriodData[currentDayIndex] += amount;
					totalAmount += amount;
				}

				// Check previous period
				const previousDayIndex = this.getDayIndex(txnDate, previousDays);
				if (previousDayIndex !== -1) {
					previousPeriodData[previousDayIndex] += amount;
					totalAmountPrevious += amount;
				}
			});

			return {
				totalAmount: totalAmount,
				totalAmountPrevious: totalAmountPrevious,
				series: [
					{
						name: this.getString('previous_period'),
						data: previousPeriodData
					},
					{
						name: this.getString('current_period'),
						data: currentPeriodData
					}
				],
				xaxis: days.map(day => day.label)
			};
		}
	},
	methods: {
		getDayIndex(date, days) {
			const dateStr = date.toDateString();
			return days.findIndex(day => day.date.toDateString() === dateStr);
		},
		fetchTransactionData() {
			if (!this.currentCard) return;

			// Calculate start date (14 days ago) to get both current and previous periods
			const startDate = new Date();
			startDate.setDate(startDate.getDate() - 14);
			const startDateStr = startDate.toISOString().split('T')[0] + ' 00:00:00';

			this.loading = true;
			this.cardsStore.getCardTransactions(this.currentCard.card_id, startDateStr, (data) => {
				this.loading = false;
				this.transactionData = data;
				// Force chart refresh
				this.refreshKey++;
			}, true); // Always force fresh data for chart
		}
	},
	watch: {
		'cardsStore.activeCardId': {
			handler(newCardId) {
				if (newCardId) {
					this.fetchTransactionData();
				}
			},
			immediate: true
		},
		'cardsStore.forceRefresh': {
			handler() {
				if (this.currentCard) {
					this.fetchTransactionData();
				}
			}
		}
	}
}
</script>

<style scoped lang="scss">

</style>